import {ChangeDetectionStrategy, Component, HostBinding, ViewChild} from '@angular/core';
import {AladinService} from '../../aladin.service';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {merge, Observable} from 'rxjs';
import {AladinMouseMove} from '../../aladin-sky/aladin-sky.component';
import {AladinPopoverSkyObjectComponent} from '../aladin-popover-sky-object/aladin-popover-sky-object.component';
import {AladinPopoverFootprintComponent} from '../aladin-popover-footprint/aladin-popover-footprint.component';
import {AladinFootprintsService} from '../../aladin-footprints.service';
import {AladinSkyObjectsService} from '../../aladin-sky-objects.service';
import {AladinPopoverContextmenuComponent} from '../aladin-popover-contextmenu/aladin-popover-contextmenu.component';
import {AsaxSkyViewService} from 'asax-common';
import { AladinPopoverSearchRadiusComponent } from '../aladin-popover-search-radius/aladin-popover-search-radius.component';

const pixelsOffset = 5;

@Component({
  selector: 'asax-sky-view-aladin-popover-container',
  templateUrl: './aladin-popover-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [':host {position: absolute;z-index: 21;}']
})
export class AladinPopoverContainerComponent {
  @ViewChild(AladinPopoverFootprintComponent, {static: true}) footprintPopover: AladinPopoverFootprintComponent;
  @ViewChild(AladinPopoverSkyObjectComponent, {static: true}) skyObjectPopover: AladinPopoverSkyObjectComponent;
  @ViewChild(AladinPopoverContextmenuComponent, {static: true}) contextmenuPopover: AladinPopoverContextmenuComponent;
  @ViewChild(AladinPopoverSearchRadiusComponent, {static: true}) searchRadiusPopover: AladinPopoverSearchRadiusComponent;

  @HostBinding('style.top.px') top: number;
  @HostBinding('style.left.px') left: number;

  mouseMove: AladinMouseMove;
  footprint: Observable<any>;
  skyObject: Observable<any>;
  searchRadius: Observable<any>;
  raDec: Observable<[number, number]>;

  constructor(private readonly asaxSkyViewService: AsaxSkyViewService,
              private readonly aladinService: AladinService,
              private readonly aladinFootprintsService: AladinFootprintsService,
              private readonly aladinSkyObjectsService: AladinSkyObjectsService) {
    this.aladinService.getMouseMove().pipe(tap((mouseMove) => this.mouseMove = mouseMove)).subscribe();

    this.footprint = this.asaxSkyViewService.getFootprintHovered().pipe(
      filter((footprint) => !!footprint),
      switchMap((footprint) => this.aladinFootprintsService.fetchFootprint(footprint.datasetId)),
      tap(() => this.top = this.mouseMove.y - pixelsOffset),
      tap(() => this.left = this.mouseMove.x - pixelsOffset),
      tap(() => this.skyObjectPopover.close()),
      tap(() => this.contextmenuPopover.close()),
      tap(() => this.searchRadiusPopover.close())
    );

    this.asaxSkyViewService.getFootprintHovered().pipe(
      filter((footprint) => !footprint),
      tap(() => this.footprintPopover.closeWithTimeout())
    ).subscribe();

    this.skyObject = this.aladinSkyObjectsService.getSkyObjectHovered().pipe(
      filter((skyObject) => !!skyObject),
      switchMap((skyObject) => this.aladinSkyObjectsService.fetchSkyObject(skyObject.id)),
      tap(() => this.top = this.mouseMove.y),
      tap(() => this.left = this.mouseMove.x),
      tap(() => this.footprintPopover.close()),
      tap(() => this.contextmenuPopover.close()),
      tap(() => this.searchRadiusPopover.close())
    );

    this.aladinSkyObjectsService.getSkyObjectHovered().pipe(
      filter((skyObject) => !skyObject),
      tap(() => this.skyObjectPopover.closeWithTimeout())
    ).subscribe();

    this.searchRadius = this.asaxSkyViewService.getSearchRadiusHovered().pipe(
      tap(() => this.top = this.mouseMove.y),
      tap(() => this.left = this.mouseMove.x),
      tap(() => this.footprintPopover.close()),
      tap(() => this.contextmenuPopover.close()),
      tap(() => this.skyObjectPopover.close())
    );

    this.raDec = merge(
      this.aladinService.getContextmenu().pipe(
        filter((mouseEvent) => !!mouseEvent),
        tap(() => this.top = this.mouseMove?.y),
        tap(() => this.left = this.mouseMove?.x),
        tap(() => this.footprintPopover.close()),
        tap(() => this.skyObjectPopover.close()),
        tap(() => this.searchRadiusPopover.close()),
        map(() => [this.mouseMove?.ra, this.mouseMove?.dec])
      ),
      this.aladinService.getContextmenu().pipe(
        filter((mouseEvent) => !mouseEvent),
        map(() => undefined))
    );
  }
}
